import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBrain, FaPencilAlt, FaClipboardList, FaChalkboardTeacher, FaFileUpload, FaTimesCircle, FaList } from 'react-icons/fa';
import { createExamWithAI, extractRawTextFromPDF } from './api';
import './style/MakeExamWithAi.css';
import { useSelector } from 'react-redux';

export default function MakeExamWithAi() {
  const [examTitle, setExamTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [selectedTypes, setSelectedTypes] = useState(['mcq', 'sa']);
  const [selectedDifficulties, setSelectedDifficulties] = useState(['medium']);
  const [pdfFile, setPdfFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [examData, setExamData] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const selectedClass = useSelector(state => state.teacherSub.selectedClass);
  const selectedSubject = useSelector(state => state.teacherSub.selectedSubject);
  const teacherId = useSelector(state => state.teacherSub.teacherId);

  const questionTypes = [
    { id: 'mcq', label: 'Multiple Choice', icon: <FaList /> },
    { id: 'sa', label: 'Short Answer', icon: <FaPencilAlt /> },
    { id: 'ess', label: 'Essay', icon: <FaClipboardList /> },
  ];

  const difficultyLevels = [
    { id: 'easy', label: 'Easy' },
    { id: 'medium', label: 'Medium' },
    { id: 'hard', label: 'Hard' }
  ];

  useEffect(() => {
    const storedExamData = localStorage.getItem('examData');
    if (storedExamData) {
      const parsedData = JSON.parse(storedExamData);
      setExamData(parsedData);
      setExamTitle(parsedData.title);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setError('');
    } else {
      setError('Please upload a PDF file');
      setPdfFile(null);
    }
  };

  const handleTypeChange = (type) => {
    setSelectedTypes(prevTypes => 
      prevTypes.includes(type)
        ? prevTypes.filter(t => t !== type)
        : [...prevTypes, type]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      let referenceText = '';

      if (pdfFile) {
        try {
          const extractedData = await extractRawTextFromPDF(pdfFile);
          referenceText = extractedData.extractedText;
        } catch (pdfError) {
          console.error('PDF extraction error:', pdfError);
          setError('Failed to extract text from PDF. Proceeding with topic only.');
        }
      }

      const result = await createExamWithAI({
        examTitle,
        topic,
        numQuestions: parseInt(numQuestions),
        questionTypes: selectedTypes,
        difficultyLevels: selectedDifficulties,
        referenceText,
        examData,
        selectedClass,
        selectedSubject,
        teacherId
      });

      if (result.success) {
        const updatedExamData = {
          ...examData,
          id: result.exam._id,
          title: result.exam.exam_title,
          questions: result.exam.questions,
          class: selectedClass,
          subject: selectedSubject
        };
        localStorage.setItem('examData', JSON.stringify(updatedExamData));
        setSuccess('Exam created successfully!');
        navigate('/exams/createWithAI/AiGeneratedExam', { state: { exam: updatedExamData } });
      } else {
        setError('Failed to create exam. Please try again.');
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Failed to create exam');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='bg-gradient-to-br from-blue-100 to-purple-100 min-h-screen'>
      <div className="unique17-make-exam-with-ai">
        <h1>
          <FaChalkboardTeacher className="unique17-title-icon" />
          Create Exam with AI
        </h1>
        <div className="unique17-ai-info">
          <FaBrain className="unique17-ai-icon" />
          <p>Our AI will generate an exam based on your specifications</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="unique17-form-group">
            <label htmlFor="examTitle">Exam Title</label>
            <input
              type="text"
              id="examTitle"
              value={examTitle}
              onChange={(e) => setExamTitle(e.target.value)}
              placeholder="Enter the title for your exam"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="unique17-form-group">
            <label htmlFor="topic">Topic/Prompt for AI</label>
            <input
              type="text"
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter the topic or paste your prompt here"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="unique17-form-group">
            <label>Reference Material (Optional)</label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <FaFileUpload className="w-5 h-5" />
                Upload PDF
              </button>
              {pdfFile && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{pdfFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setPdfFile(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimesCircle className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div className="unique17-form-group">
            <label htmlFor="numQuestions">Number of Questions</label>
            <input
              type="number"
              id="numQuestions"
              value={numQuestions}
              onChange={(e) => setNumQuestions(Math.max(1, parseInt(e.target.value)))}
              min="1"
              max="20"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="unique17-form-group">
            <label>Question Types</label>
            <div className="unique17-question-types">
              {questionTypes.map((type) => (
                <label key={type.id} className={`unique17-type-option ${selectedTypes.includes(type.id) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(type.id)}
                    onChange={() => handleTypeChange(type.id)}
                    className="hidden"
                  />
                  {type.icon}
                  <span>{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="unique17-form-group">
            <label>Difficulty Level</label>
            <div className="grid grid-cols-3 gap-2">
              {difficultyLevels.map((level) => (
                <label
                  key={level.id}
                  className={`flex items-center justify-center p-2 border rounded-md cursor-pointer transition-colors ${
                    selectedDifficulties.includes(level.id)
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedDifficulties.includes(level.id)}
                    onChange={(e) => {
                      setSelectedDifficulties(prev =>
                        e.target.checked
                          ? [...prev, level.id]
                          : prev.filter(d => d !== level.id)
                      )
                    }}
                    className="hidden"
                  />
                  <span className="text-sm">{level.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="unique17-form-group">
            <label>Class and Subject</label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={selectedClass}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                placeholder="Class"
              />
              <input
                type="text"
                value={selectedSubject}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                placeholder="Subject"
              />
            </div>
          </div>

          {error && <div className="text-red-500">{error}</div>}
          {success && <div className="text-green-500">{success}</div>}

          <button
            type="submit"
            disabled={isLoading || !examTitle || !topic || selectedTypes.length === 0 || selectedDifficulties.length === 0}
            className={`w-full py-3 px-4 rounded-md text-white font-medium flex items-center justify-center gap-2 ${
              isLoading || !examTitle || !topic || selectedTypes.length === 0 || selectedDifficulties.length === 0
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Creating Exam...' : 'Create Exam'}
          </button>
        </form>
      </div>
    </div>
  );
}

